import React, { useState, useEffect } from 'react';
import { ShieldCheck, RefreshCw, Save, ShieldAlert, Users, Search, User, ShieldOff, Activity, Info } from 'lucide-react';
import adminService from '../../services/adminService';

const MFAControlCenter = () => {
    const [users, setUsers] = useState([]);
    const [governance, setGovernance] = useState({
        recoveryCodeCount: '10',
        mfaLockoutAttempts: '5',
        mfaLockoutDuration: '15'
    });
    const [isLoading, setIsLoading] = useState(false);
    const [statusMessage, setStatusMessage] = useState({ text: '', type: '' });
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [usersData, policiesData] = await Promise.all([
                adminService.getUsers(),
                adminService.getPolicies()
            ]);
            setUsers(usersData);
            if (policiesData && policiesData.security) {
                setGovernance(policiesData.security);
            }
        } catch (err) {
            console.error("Failed to fetch admin data", err);
            showMessage("Failed to sync with backend.", "error");
        }
    };

    const showMessage = (text, type) => {
        setStatusMessage({ text, type });
        setTimeout(() => setStatusMessage({ text: '', type: '' }), 4000);
    };



    const handleResetMfa = async (userId) => {
        if (!window.confirm("Are you sure? This will force the user to re-configure their MFA device.")) return;
        try {
            await adminService.resetUserMfa(userId);
            showMessage("MFA secret reset successfully.", "success");
            fetchData();
        } catch (err) {
            showMessage("Failed to reset MFA.", "error");
        }
    };

    const handleSaveGovernance = async () => {
        setIsLoading(true);
        try {
            await adminService.updatePolicies('security', governance);
            showMessage("Global MFA policies updated.", "success");
        } catch (err) {
            showMessage("Failed to save policies.", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const filteredUsers = users.filter(u => 
        u.mail.toLowerCase().includes(searchTerm.toLowerCase()) || 
        (u.fullName && u.fullName.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="space-y-8 animate-fade-in pb-10">
            {/* Header Section */}
            <header className="flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-extrabold tracking-tight underline decoration-[#4f46e5]/30 decoration-4 underline-offset-8 uppercase tracking-wider">
                        2MFA Governance
                    </h1>
                    <p className="text-slate-500 mt-2 font-medium">
                        Enterprise multi-factor governance & security overrides.
                    </p>
                </div>
                <div className="flex items-center space-x-3">
                    {statusMessage.text && (
                        <div className={`px-4 py-2 rounded-2xl text-[10px] font-black uppercase tracking-widest animate-pulse shadow-sm ${statusMessage.type === 'success' ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-500 border border-rose-500/20'}`}>
                            {statusMessage.text}
                        </div>
                    )}
                    <div className="flex items-center text-[#4f46e5] bg-[#4f46e5]/10 px-4 py-2 rounded-2xl text-[10px] font-bold uppercase tracking-[0.1em] border border-[#4f46e5]/20 shadow-sm">
                        <ShieldCheck size={14} className="mr-2 text-emerald-500" />
                        Infrastructure Secure
                    </div>
                </div>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Global Governance Panel */}
                <div className="lg:col-span-1 space-y-6">
                    <div className="enterprise-card p-8 glass relative overflow-hidden shadow-xl border-slate-200/60 dark:border-slate-800/60">
                        <div className="absolute top-0 left-0 w-full h-1 bg-[#4f46e5]"></div>
                        <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none text-[#4f46e5]">
                            <ShieldAlert size={100} />
                        </div>
                        
                        <div className="flex items-center gap-3 mb-8">
                            <div className="w-10 h-10 rounded-xl bg-indigo-500/10 text-indigo-500 flex items-center justify-center">
                                <ShieldAlert size={20} />
                            </div>
                            <div>
                                <h2 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-800 dark:text-slate-200 leading-tight">Global Policies</h2>
                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">MTA Security engine</p>
                            </div>
                        </div>
                        
                        <div className="space-y-6">
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 px-1">Recovery Code Count</label>
                                <select 
                                    className="premium-input w-full h-12 text-sm font-bold shadow-sm"
                                    value={governance.recoveryCodeCount}
                                    onChange={(e) => setGovernance({...governance, recoveryCodeCount: e.target.value})}
                                >
                                    <option value="8">8 Codes (Legacy)</option>
                                    <option value="10">10 Codes (Standard)</option>
                                    <option value="12">12 Codes (High Security)</option>
                                    <option value="16">16 Codes (Maximum)</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 px-1">Max Failed Attempts</label>
                                <div className="relative group">
                                    <ShieldOff className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={16} />
                                    <input 
                                        type="number"
                                        className="premium-input w-full pl-12 h-12 text-sm font-bold shadow-sm"
                                        value={governance.mfaLockoutAttempts}
                                        onChange={(e) => setGovernance({...governance, mfaLockoutAttempts: e.target.value})}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 px-1">Lockout Minutes</label>
                                <div className="relative group">
                                    <Activity className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={16} />
                                    <input 
                                        type="number"
                                        className="premium-input w-full pl-12 h-12 text-sm font-bold shadow-sm"
                                        value={governance.mfaLockoutDuration}
                                        onChange={(e) => setGovernance({...governance, mfaLockoutDuration: e.target.value})}
                                    />
                                </div>
                            </div>

                            <button 
                                onClick={handleSaveGovernance}
                                disabled={isLoading}
                                className="premium-button-primary w-full mt-4 h-14 rounded-2xl flex items-center justify-center gap-3 shadow-xl shadow-indigo-500/20"
                            >
                                <Save size={18} />
                                <span className="text-[10px] font-black uppercase tracking-widest">
                                    {isLoading ? 'Processing...' : 'Save Global Config'}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* User Overrides Panel */}
                <div className="lg:col-span-2">
                    <div className="enterprise-card glass overflow-hidden relative shadow-xl border-slate-200/60 dark:border-slate-800/60">
                        <div className="absolute top-0 left-0 w-full h-1 bg-[#4f46e5]"></div>
                        
                        <div className="p-6 border-b border-[rgb(var(--border))] flex items-center justify-between gap-4 glass">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-indigo-500/10 text-indigo-500 flex items-center justify-center">
                                    <Users size={20} />
                                </div>
                                <div>
                                    <h2 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-800 dark:text-slate-200 leading-tight">User Overrides</h2>
                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">Identity Access Plane</p>
                                </div>
                            </div>
                            <div className="relative flex-1 max-w-xs">
                                <div className="premium-input flex items-center h-12 px-4 shadow-sm">
                                    <Search size={16} className="text-slate-400 mr-2" />
                                    <input 
                                        type="text" 
                                        placeholder="Search identity..." 
                                        className="bg-transparent border-none p-0 focus:ring-0 text-sm w-full placeholder:text-slate-400 font-medium"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2 text-slate-400 text-[10px] font-black uppercase tracking-widest opacity-50">
                                    {filteredUsers.length} Active
                                </div>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-slate-50 dark:bg-slate-900/50 border-b border-[rgb(var(--border))]">
                                    <tr>
                                        <th className="px-6 py-4 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">User Identity</th>
                                        <th className="px-6 py-4 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">MFA Status</th>
                                        <th className="px-6 py-4 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 text-right">Goverance Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[rgb(var(--border))]">
                                    {filteredUsers.map(user => (
                                        <tr key={user.id} className="hover:bg-[#4f46e5]/[0.03] transition-all group border-l-4 border-l-transparent hover:border-l-indigo-500">
                                            <td className="px-6 py-5">
                                                <div className="flex items-center space-x-4">
                                                    <div className="w-11 h-11 rounded-2xl bg-white dark:bg-slate-800 text-slate-500 group-hover:text-[#4f46e5] group-hover:bg-[#4f46e5]/10 border border-[rgb(var(--border))] flex items-center justify-center transition-all shadow-sm">
                                                        <User size={22} />
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-black text-slate-900 dark:text-slate-100 uppercase tracking-tight leading-tight">
                                                            {user.fullName || 'Unnamed Employee'}
                                                        </span>
                                                        <span className="text-[10px] text-slate-400 font-bold font-mono tracking-tighter mt-1 italic uppercase">{user.mail}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-5">
                                                {user.twoFactorEnabled ? (
                                                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 text-[9px] font-black uppercase tracking-widest shadow-sm">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)] anim-pulse" />
                                                        Protected
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-500/10 text-slate-500 border border-slate-500/20 text-[9px] font-black uppercase tracking-widest shadow-sm">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                                                        Standard
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-5 text-right">
                                                <div className="flex items-center justify-end gap-3">

                                                    <button 
                                                        onClick={() => handleResetMfa(user.id)}
                                                        className="p-2.5 text-rose-500 border border-red-100 dark:border-red-900/30 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-xl transition-all shadow-sm"
                                                        title="Force Reset MFA"
                                                    >
                                                        <RefreshCw size={18} />
                                                    </button>
                                                    <button className="p-2.5 text-slate-400 border border-slate-100 dark:border-slate-900 hover:bg-slate-50 dark:hover:bg-slate-900 rounded-xl transition-all shadow-sm">
                                                        <Info size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {filteredUsers.length === 0 && (
                                <div className="py-24 text-center">
                                    <div className="w-16 h-16 bg-slate-50 dark:bg-slate-900 rounded-2xl flex items-center justify-center text-slate-300 mx-auto mb-4 border border-[rgb(var(--border))]">
                                        <ShieldOff size={32} />
                                    </div>
                                    <p className="text-sm font-black text-slate-400 uppercase tracking-widest">No matching identities found</p>
                                    <p className="text-xs text-slate-300 mt-2 font-medium">Verify your search criteria or filter scope</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MFAControlCenter;
